/**
 * Service Worker registration with a "tap to update" banner.
 *
 * Goals:
 * - Proactively check for SW updates on each page load
 * - When a new SW is installed and waiting, show a banner
 * - On banner click, tell the waiting SW to skipWaiting (Workbox listens for SKIP_WAITING)
 * - Reload once the new SW takes control (controllerchange)
 * - Avoid duplicate banners and clean up banner if the waiting worker activates/redundant
 */
const UPDATE_BANNER_ID = 'sw-update-banner';

function showServiceWorkerUpdateBanner(swRegistration: ServiceWorkerRegistration) {
  if (document.getElementById(UPDATE_BANNER_ID)) return;

  const updateBanner = document.createElement('button');
  updateBanner.id = UPDATE_BANNER_ID;

  updateBanner.style.position = 'fixed';
  updateBanner.style.top = '0px';
  updateBanner.style.left = '0px';
  updateBanner.style.zIndex = '9999';
  updateBanner.style.background = 'white';
  updateBanner.style.height = '70px';
  updateBanner.style.padding = '10px';
  updateBanner.style.width = '100%';
  updateBanner.style.borderBottom = '1px solid #CBD5E1';

  updateBanner.innerHTML =
    '<div class="leading-snug">A new update is available. <a>Tap to update</a></div>';

  const removeUpdateBanner = () => updateBanner.remove();

  // If there is a waiting SW, watch it: when it activates or becomes redundant, remove banner.
  // (This handles cases where activation happens without a click.)
  const waitingWorker = swRegistration.waiting;
  if (waitingWorker) {
    waitingWorker.addEventListener('statechange', () => {
      // When it activates, the page will also reload via controllerchange listener.
      if (waitingWorker.state === 'activated' || waitingWorker.state === 'redundant') {
        removeUpdateBanner();
      }
    });
  }

  // Clicking triggers activation; hide immediately to avoid double taps.
  updateBanner.addEventListener('click', () => {
    const workerWaitingToActivate = swRegistration.waiting;
    if (!workerWaitingToActivate) return;

    updateBanner.disabled = true;
    removeUpdateBanner();

    workerWaitingToActivate.postMessage({ type: 'SKIP_WAITING' });
  });

  document.body.appendChild(updateBanner);
}

/**
 * Invokes callback when a newly-installed Service Worker is ready and waiting.
 * - If reg.waiting already exists, callback immediately.
 * - Otherwise, listen for updatefound -> installing -> installed.
 */
function onNewServiceWorkerWaiting(
  swRegistration: ServiceWorkerRegistration,
  onWaitingReady: () => void
) {
  // If there's already a waiting SW, we're done.
  if (swRegistration.waiting) {
    onWaitingReady();
    return;
  }

  const attachInstallingStateListener = () => {
    const installingWorker = swRegistration.installing;
    if (!installingWorker) return;

    installingWorker.addEventListener('statechange', (event: Event) => {
      const worker = event.target as ServiceWorker | null;

      // "installed" means: either it's waiting (update case) OR it's the first install.
      // We only want to show the banner when there's actually a waiting worker.
      if (worker?.state === 'installed' && swRegistration.waiting) {
        onWaitingReady();
      }
    });
  };

  // If we're already installing, attach now; otherwise wait until updatefound.
  if (swRegistration.installing) {
    attachInstallingStateListener();
  } else {
    swRegistration.addEventListener('updatefound', attachInstallingStateListener);
  }
}

/**
 * Register the service worker and wire up "manual update" UX.
 *
 * @param swFileBaseName e.g. "service-worker" (will register "/service-worker.js")
 * @param swScope e.g. "/" or "/app/"
 */
export function registerServiceWorker(swFileBaseName: string, swScope: string) {
  // Prevent infinite reload loops; controllerchange can fire multiple times in some edge cases.
  let hasReloadedForNewController = false;

  // Reload page once the new SW takes control
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hasReloadedForNewController) return;
    hasReloadedForNewController = true;
    window.location.reload();
  });

  navigator.serviceWorker
    .register(`/${swFileBaseName}.js`, {
      scope: swScope,

      // Ensures the SW script is fetched fresh instead of relying on HTTP cache.
      // This dramatically reduces "my SW didn't update" issues.
      updateViaCache: 'none',
    })
    .then(async swRegistration => {
      // Proactively check for updates on load.
      // Note: This is safe even if nothing changed; it’s just a check.
      try {
        await swRegistration.update();
      } catch (error) {
        console.error('Service Worker update check failed:', error);
      }

      // If this tab is not currently controlled, it's the first install for this tab.
      // Don't show update UI (there’s nothing to update yet).
      if (!navigator.serviceWorker.controller) return;

      // When a new SW becomes waiting, show the banner.
      onNewServiceWorkerWaiting(swRegistration, () => {
        showServiceWorkerUpdateBanner(swRegistration);
      });
    })
    .catch(error => {
      console.error('Error registering the Service Worker:', error);
    });
}
